import {
  Box,
  Button as ChakraButton,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react';
import {
  faChevronDown,
  faEdit,
  faTrash,
  faWarning
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonIcon,
  FormFeedback,
  Input,
  Modal,
  OutlineCard,
  TextArea,
  useToast
} from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  fetchJournalConfiguredDecisionTemplates,
  fetchJournalConfiguredResponseTemplates
} from 'src/api/queries';
import {
  useDeleteJournalDecisionTemplate,
  useSaveJournalDecisionTemplate
} from 'src/api/mutations';
import { Overlay } from 'src/components/Overlay';
import { FieldLabel } from 'src/components/Field';

interface ResponseTemplateFormModalProps {
  isOpen: boolean;
  journalId: string;
  templateType: string;
  decisionType: string;
  decisionLabel: string;
  onClose: () => void;
}

interface ConfiguredDecisionTemplate {
  templateId: string;
  text: string;
  label: string;
  type: string;
  subtype?: string;
  templateType: string;
}

interface ResponseTemplateFormData {
  templateId: string;
  label: string;
  text: string;
  subtype: string;
}

type EditorMode = 'none' | 'create' | 'edit';

const EMPTY_FORM: ResponseTemplateFormData = {
  templateId: '',
  label: '',
  text: '',
  subtype: ''
};

const LABEL_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 10000;

const ResponseTemplateFormModal: React.FC<ResponseTemplateFormModalProps> = ({
  isOpen,
  journalId,
  templateType,
  decisionType,
  decisionLabel,
  onClose
}) => {
  const toast = useToast();
  const saveMutation = useSaveJournalDecisionTemplate(journalId);
  const deleteMutation = useDeleteJournalDecisionTemplate(journalId);

  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [editorMode, setEditorMode] = useState<EditorMode>('none');
  const [templateToDelete, setTemplateToDelete] =
    useState<ConfiguredDecisionTemplate | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    formState: { errors }
  } = useForm<ResponseTemplateFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: EMPTY_FORM
  });

  const { data: decisionOptionsData, isLoading: isDecisionOptionsLoading } =
    useQuery({
      queryKey: [
        'journal-configuration-response-template-options',
        journalId,
        templateType
      ],
      queryFn: () =>
        fetchJournalConfiguredResponseTemplates(journalId, templateType),
      enabled: isOpen
    });

  const {
    data: configuredTemplatesData,
    isLoading: isConfiguredTemplatesLoading
  } = useQuery({
    queryKey: [
      'journal-configuration-decision-templates',
      journalId,
      templateType
    ],
    queryFn: () =>
      fetchJournalConfiguredDecisionTemplates(journalId, templateType),
    enabled: isOpen
  });

  const decisionOptions = useMemo(
    () => decisionOptionsData?.decisions ?? [],
    [decisionOptionsData]
  );

  const currentDecisionOption = useMemo(
    () => decisionOptions.find(option => option.type === decisionType),
    [decisionOptions, decisionType]
  );

  const currentTemplates = useMemo(() => {
    const groupedTemplates = configuredTemplatesData?.decisionTemplates ?? [];
    const entry = groupedTemplates.find(
      template => template.type === decisionType
    );
    if (!entry) return [];

    return (
      entry.templates ??
      entry.subtypes?.flatMap(subtype => subtype.templates) ??
      []
    );
  }, [configuredTemplatesData, decisionType]);

  const selectedTemplate = useMemo(
    () =>
      currentTemplates.find(
        template => template.templateId === selectedTemplateId
      ),
    [currentTemplates, selectedTemplateId]
  );

  const availableSubtypes = currentDecisionOption?.subtypes ?? [];

  const resetFormFromTemplate = useCallback(
    (template?: ConfiguredDecisionTemplate) => {
      reset({
        templateId: template?.templateId ?? '',
        label: template?.label ?? '',
        text: template?.text ?? '',
        subtype: template?.subtype ?? ''
      });
    },
    [reset]
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedTemplateId('');
      setEditorMode('none');
      resetFormFromTemplate();
      clearErrors();
      return;
    }

    resetFormFromTemplate(selectedTemplate);
  }, [clearErrors, isOpen, resetFormFromTemplate, selectedTemplate]);

  const handleCreateNew = useCallback(() => {
    setSelectedTemplateId('');
    setEditorMode('create');
    resetFormFromTemplate();
    clearErrors();
  }, [clearErrors, resetFormFromTemplate]);

  const handleSelectTemplate = useCallback(
    (template: ConfiguredDecisionTemplate) => {
      setSelectedTemplateId(template.templateId);
      setEditorMode('edit');
      resetFormFromTemplate(template);
      clearErrors();
    },
    [clearErrors, resetFormFromTemplate]
  );

  const handleSave = useCallback(
    async (formValues: ResponseTemplateFormData) => {
      const response = await saveMutation.mutateAsync({
        templateId: selectedTemplateId || undefined,
        label: formValues.label.trim(),
        text: formValues.text.trim(),
        type: decisionType,
        subtype:
          availableSubtypes.length > 0
            ? formValues.subtype.trim() || undefined
            : undefined,
        templateType
      });

      if (!response.isSuccess) {
        const message =
          typeof response?.message === 'string'
            ? response.message
            : response?.message?.errors?.[0]?.reason ||
              response?.message?.message ||
              'Failed to save response template';

        toast({
          variant: 'error',
          title: 'Failed to save response template',
          description: message,
          isClosable: true
        });
        return;
      }

      toast({
        variant: 'success',
        title: 'Response template saved successfully',
        isClosable: true
      });

      setSelectedTemplateId('');
      setEditorMode('none');
      resetFormFromTemplate();
      clearErrors();
    },
    [availableSubtypes.length, decisionType, saveMutation, templateType, toast]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!templateToDelete) return;

    const response = await deleteMutation.mutateAsync({
      templateId: templateToDelete.templateId
    });

    if (!response.isSuccess) {
      const message =
        typeof response?.message === 'string'
          ? response.message
          : response?.message?.errors?.[0]?.reason ||
            response?.message?.message ||
            'Failed to delete response template';

      toast({
        variant: 'error',
        title: 'Failed to delete response template',
        description: message,
        isClosable: true
      });
      return;
    }

    if (selectedTemplateId === templateToDelete.templateId) {
      setSelectedTemplateId('');
      setEditorMode('none');
      resetFormFromTemplate();
      clearErrors();
    }

    setTemplateToDelete(null);

    toast({
      variant: 'success',
      title: 'Response template deleted successfully',
      isClosable: true
    });
  }, [
    clearErrors,
    deleteMutation,
    resetFormFromTemplate,
    selectedTemplateId,
    templateToDelete,
    toast
  ]);

  const handleCancelEdit = useCallback(() => {
    setSelectedTemplateId('');
    setEditorMode('none');
    resetFormFromTemplate();
    clearErrors();
  }, [clearErrors, resetFormFromTemplate]);

  const handleClose = useCallback(() => {
    setSelectedTemplateId('');
    setEditorMode('none');
    resetFormFromTemplate();
    clearErrors();
    onClose();
  }, [clearErrors, onClose, resetFormFromTemplate]);

  if (!isOpen) return null;

  return (
    <>
      <Overlay height="100%">
        <Flex
          flexDirection="column"
          backgroundColor="surfaceSecondaryFill"
          minH="100vh"
        >
          <Flex
            direction="column"
            gap={6}
            mx={{ base: 4, md: 8 }}
            mt={{ base: 4, md: 8 }}
            paddingBottom="220px"
          >
            <Flex alignItems="center" justifyContent="space-between" gap={3}>
              <Flex direction="column">
                <Text textStyle="headingSection">Pre-filled Responses to Author</Text>
                <Text textStyle="textBody">
                  Configure pre-filled response templates for{' '}
                  <Text as="span" fontWeight="semibold">
                    {decisionLabel}
                  </Text>
                </Text>
              </Flex>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCreateNew}
              >
                New Template
              </Button>
            </Flex>

            {(isDecisionOptionsLoading || isConfiguredTemplatesLoading) && (
              <Flex justify="center" align="center" paddingY={20}>
                <Spinner color="#1D4ED8" thickness="3px" />
              </Flex>
            )}

            {!isDecisionOptionsLoading && !isConfiguredTemplatesLoading && (
              <OutlineCard borderless paddingX={4} paddingY={6}>
                {currentTemplates.length === 0 ? (
                  <Flex
                    flexDirection="column"
                    gap={2}
                    alignItems="center"
                    paddingY={8}
                  >
                    <Text textStyle="headingGroup">
                      No templates configured
                    </Text>
                    <Text textStyle="textBody">
                      No response templates have been configured yet.
                    </Text>
                  </Flex>
                ) : (
                  <Box overflowX="auto" width="100%">
                    <Table>
                      <Thead>
                        <Tr
                          sx={{
                            '& th': {
                              textTransform: 'none',
                              color: 'black',
                              background: 'white'
                            }
                          }}
                        >
                          <Th p={4}>
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Label
                            </Text>
                          </Th>
                          <Th p={4}>
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Description
                            </Text>
                          </Th>
                          {availableSubtypes.length > 0 && (
                            <Th p={4}>
                              <Text textStyle="headingItem" whiteSpace="nowrap">
                                Options
                              </Text>
                            </Th>
                          )}
                          <Th width="100px" textAlign="center">
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Actions
                            </Text>
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {currentTemplates.map(template => (
                          <Tr
                            key={template.templateId}
                            _last={{ borderBottom: 'none' }}
                          >
                            <Td maxWidth="320px">
                              <Text textStyle="headingItem"  wordBreak="break-word">
                                {template.label}
                              </Text>
                            </Td>
                            <Td maxWidth="320px">
                              <Text textStyle="textContext"  wordBreak="break-word">
                                {template.text}
                              </Text>
                            </Td>
                            {availableSubtypes.length > 0 && (
                              <Td maxWidth="320px">
                                <Text
                                  textStyle="textContext"
                                  whiteSpace="normal"
                                  wordBreak="break-word"
                                >
                                  {availableSubtypes.find(
                                    subtype => subtype.type === template.subtype
                                  )?.label ?? '—'}
                                </Text>
                              </Td>
                            )}
                            <Td>
                              <Flex justifyContent="center" gap={2}>
                                <ButtonIcon
                                  variant="secondary"
                                  size="small"
                                  ariaLabel={`Edit template ${template.label}`}
                                  icon={<FontAwesomeIcon icon={faEdit} />}
                                  onClick={() => handleSelectTemplate(template)}
                                />
                                <ButtonIcon
                                  variant="caution"
                                  size="small"
                                  ariaLabel={`Delete template ${template.label}`}
                                  icon={
                                    <FontAwesomeIcon
                                      icon={faTrash}
                                      style={{ color: 'red' }}
                                    />
                                  }
                                  onClick={() => setTemplateToDelete(template)}
                                />
                              </Flex>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </OutlineCard>
            )}
          </Flex>
        </Flex>
        <Flex
          padding={4}
          bottom={0}
          zIndex={1}
          justifyContent="flex-end"
          position="sticky"
          bgColor="surfacePrimaryFill"
          marginX="auto"
          width="100%"
        >
          <Flex gap={4}>
            <Button type="button" variant="caution" onClick={handleClose}>
              Exit
            </Button>
          </Flex>
        </Flex>
      </Overlay>

      {editorMode !== 'none' && (
        <Modal
          isOpen={true}
          onClose={handleCancelEdit}
          width="45rem"
          closeButtonProps={{ hidden: true }}
        >
          <Flex
            as="form"
            flexDirection="column"
            gap={4}
            padding={8}
            onSubmit={handleSubmit(handleSave)}
          >
            <Text textStyle="headingGroup">
              {editorMode === 'edit' ? 'Edit template' : 'Create template'}
            </Text>

            <Flex
              flexDirection="column"
              alignItems="flex-start"
              gap={2}
              width="100%"
            >
              <FieldLabel
                label="Label"
                subText=""
                isRequired={true}
                helpText=""
              />
              <Flex
                flexDirection="column"
                width="100%"
                alignItems="flex-start"
                gap={2}
              >
                <Controller
                  control={control}
                  name="label"
                  rules={{
                    required: 'Label is required',
                    maxLength: {
                      value: LABEL_MAX_LENGTH,
                      message: `Label cannot exceed ${LABEL_MAX_LENGTH} characters`
                    }
                  }}
                  render={({ field }) => (
                    <Input
                      id="label"
                      {...field}
                      isInvalid={!!errors.label}
                      placeholder="Template label"
                      onChange={event => {
                        field.onChange(event);
                        if (event.target.value.length > LABEL_MAX_LENGTH) {
                          setError('label', {
                            type: 'maxLength',
                            message: `Label cannot exceed ${LABEL_MAX_LENGTH} characters`
                          });
                        } else if (errors.label) {
                          clearErrors('label');
                        }
                      }}
                      onBlur={event => {
                        field.onBlur();
                        field.onChange(event.target.value.trim());
                      }}
                    />
                  )}
                />
                {errors.label && (
                  <FormFeedback variant="error">
                    {errors.label.message}
                  </FormFeedback>
                )}
              </Flex>
            </Flex>

            <Flex
              flexDirection="column"
              alignItems="flex-start"
              gap={2}
              width="100%"
            >
              <FieldLabel
                label="Option"
                subText=""
                isRequired={availableSubtypes.length > 0}
                helpText=""
              />
              <Flex
                flexDirection="column"
                width="100%"
                alignItems="flex-start"
                gap={2}
              >
                <Controller
                  control={control}
                  name="subtype"
                  rules={{
                    validate: value =>
                      availableSubtypes.length === 0 || value.trim()
                        ? true
                        : 'Option is required'
                  }}
                  render={({ field }) => {
                    const selectedSubtype = availableSubtypes.find(
                      subtype => subtype.type === field.value
                    );

                    return (
                      <Menu matchWidth>
                        <MenuButton
                          as={ChakraButton}
                          rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
                          textAlign="left"
                          justifyContent="space-between"
                          fontWeight="normal"
                          width="100%"
                          height="40px"
                          px={2}
                          borderWidth="1px"
                          borderRadius="small"
                          borderColor={
                            errors.subtype
                              ? 'errorPrimaryBorder'
                              : 'inputBorder'
                          }
                          backgroundColor={
                            errors.subtype ? 'errorPrimaryFill' : 'inputFill'
                          }
                          color="inputContent"
                          _hover={{ borderColor: 'inputBorderHover' }}
                          _active={{ borderColor: 'inputBorderActive' }}
                          isDisabled={availableSubtypes.length === 0}
                          _disabled={{
                            backgroundColor: 'inputFillReadOnly',
                            color: 'inputContentReadOnly',
                            cursor: 'not-allowed',
                            borderColor: 'inputBorderReadOnly'
                          }}
                        >
                          <Box
                            as="span"
                            display="block"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                            color={
                              selectedSubtype
                                ? 'inputContent'
                                : 'inputContentReadOnly'
                            }
                          >
                            {selectedSubtype?.label ??
                              (availableSubtypes.length > 0
                                ? 'Select option'
                                : 'No option')}
                          </Box>
                        </MenuButton>
                        <MenuList maxH="320px" overflowY="auto">
                          {availableSubtypes.map(subtype => (
                            <MenuItem
                              key={subtype.type}
                              onClick={() => {
                                field.onChange(subtype.type);
                                if (errors.subtype) clearErrors('subtype');
                              }}
                              whiteSpace="normal"
                            >
                              {subtype.label}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    );
                  }}
                />
                {errors.subtype && (
                  <FormFeedback variant="error">
                    {errors.subtype.message}
                  </FormFeedback>
                )}
              </Flex>
            </Flex>

            <Flex
              flexDirection="column"
              alignItems="flex-start"
              gap={2}
              width="100%"
            >
              <FieldLabel
                label="Description"
                subText=""
                isRequired={true}
                helpText=""
              />
              <Flex
                flexDirection="column"
                width="100%"
                alignItems="flex-start"
                gap={2}
              >
                <Controller
                  control={control}
                  name="text"
                  rules={{
                    required: 'Description is required',
                    maxLength: {
                      value: DESCRIPTION_MAX_LENGTH,
                      message: `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters`
                    }
                  }}
                  render={({ field }) => (
                    <TextArea
                      id="text"
                      {...field}
                      isInvalid={!!errors.text}
                      placeholder="Template Description"
                      rows={6}
                      onChange={event => {
                        field.onChange(event);
                        if (event.target.value.length > DESCRIPTION_MAX_LENGTH) {
                          setError('text', {
                            type: 'maxLength',
                            message: `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters`
                          });
                        } else if (errors.text) {
                          clearErrors('text');
                        }
                      }}
                      onBlur={event => {
                        field.onBlur();
                        field.onChange(event.target.value.trim());
                      }}
                    />
                  )}
                />
                {errors.text && (
                  <FormFeedback variant="error">
                    {errors.text.message}
                  </FormFeedback>
                )}
              </Flex>
            </Flex>

            <Divider />

            <Flex justifyContent="flex-end" gap={2}>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>

              <Button type="submit" isLoading={saveMutation.isPending}>
                Save
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}

      {templateToDelete && (
        <Modal
          isOpen={true}
          onClose={() => setTemplateToDelete(null)}
          width="448px"
          closeButtonProps={{ hidden: true }}
        >
          <Flex padding={8} gap={6} flexDirection="column">
            <Flex flexDirection="column" gap={2}>
              <Text
                textStyle="headingGroup"
                color="actionSecondaryCautionContent"
              >
                <FontAwesomeIcon icon={faWarning} /> Delete template?
              </Text>
              <Text textStyle="textBody">
                Are you sure you want to delete{' '}
                <Text as="span" fontWeight="semibold">
                  {templateToDelete.label}
                </Text>
                ? This action cannot be undone.
              </Text>
            </Flex>

            <Flex gap={2} justifyContent="flex-end">
              <Button
                variant="secondary"
                onClick={() => setTemplateToDelete(null)}
              >
                Cancel
              </Button>
              <Button
                variant="caution"
                onClick={handleConfirmDelete}
                isLoading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
    </>
  );
};

export default ResponseTemplateFormModal;
